import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, Typography, Card, CardContent, Button, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { redirectToTenant } from '../utils/tenantResolver';
import { markOnboardingPendingForNextLogin } from '../utils/onboardingStorage';

const DASHBOARD_PATH = '/dashboard';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoading: authLoading } = useAuth();
  const { isMainDomain } = useTenant();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(2);
  const [orgSlugFromPayment, setOrgSlugFromPayment] = useState<string | null>(null);
  
  const sessionId = searchParams.get('session_id');

  // Clear all client-side storage (localStorage, sessionStorage, cookies) on the main domain
  const clearClientStorage = () => {
    try {
      localStorage.clear();
    } catch {}
    try {
      sessionStorage.clear();
    } catch {}
    try {
      const cookies = document.cookie ? document.cookie.split(';') : [];
      cookies.forEach((cookie) => {
        const eqPos = cookie.indexOf('=');
        const name = (eqPos > -1 ? cookie.substring(0, eqPos) : cookie).trim();
        if (name) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        }
      });
    } catch {}
  };

  useEffect(() => {
    if (sessionId) {
      // Call backend to verify and process payment
      verifyPayment(sessionId);
    } else {
      setError('Missing session ID');
      setLoading(false);
    }
  }, [sessionId]);

  const redirectToSubscription = () => {
    if (isMainDomain) {
      const userStr = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      const orgSlug = orgSlugFromPayment || (userStr ? (() => {
        try {
          const u = JSON.parse(userStr);
          return u.organizationSlug;
        } catch { return null; }
      })() : null);

      // Snapshot values we need before clearing storage
      const storedToken = token || '';
      const storedOrgSlug = orgSlug || '';

      // After successful payment on the main domain, clear all cached session data
      markOnboardingPendingForNextLogin();
      clearClientStorage();

      const loginPath = `/login?onboarding=1&returnUrl=${encodeURIComponent('/proxy-settings')}`;

      if (storedToken && storedOrgSlug) {
        apiService.createExchangeToken({
          token: storedToken,
          organizationSlug: storedOrgSlug
        }).then(({ exchangeToken }) => {
          redirectToTenant(
            storedOrgSlug,
            loginPath,
            exchangeToken
          );
        }).catch(() => {
          navigate('/login?onboarding=1');
        });
      } else {
        navigate('/login?onboarding=1');
      }
    } else {
      navigate(DASHBOARD_PATH, { replace: true });
    }
  };

  // Auto-redirect after 2 seconds when payment is successful
  useEffect(() => {
    if (success) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            redirectToSubscription();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [success, isMainDomain, orgSlugFromPayment]);

  const verifyPayment = async (sessionId: string) => {
    try {
      // Use apiService to verify payment
      const data = await apiService.verifyPayment(sessionId);

      if (!data.error && data.message) {
        setSuccess(true);
        if (data.organizationSlug) {
          setOrgSlugFromPayment(data.organizationSlug);
        }
        
        // Update user's plan type and status in localStorage if available
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            if (data.planType) {
              user.planType = data.planType;
            }
            if (data.organizationSlug && !user.organizationSlug) {
              user.organizationSlug = data.organizationSlug;
            }
            // User and organization should now be ACTIVE after payment
            localStorage.setItem('user', JSON.stringify(user));
          } catch (e) {
            console.error('Error updating user data:', e);
          }
        }

        // Set flag to trigger invoice refresh when user navigates to Billing & Invoices
        // This flag will be checked by the Invoices component
        localStorage.setItem('paymentCompleted', 'true');
        localStorage.setItem('paymentCompletedTime', Date.now().toString());

        // Success state is set, useEffect will handle the redirect with countdown
      } else {
        setError(data.error || 'Failed to verify payment');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred while verifying payment. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 8, textAlign: 'center' }}>
        {loading && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Verifying payment...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we confirm your payment
              </Typography>
            </CardContent>
          </Card>
        )}

        {success && (
          <Card>
            <CardContent>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Payment Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Your subscription has been activated. You can now enjoy all the features of your new plan.
              </Typography>
              <Alert severity="info" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Autopay Enabled:</strong> Your subscription will automatically renew at the end of each billing period. 
                  You can manage your payment methods and billing settings anytime from the Billing & Invoices section.
                </Typography>
              </Alert>
              <Typography variant="body2" color="text.secondary" paragraph>
                {isMainDomain
                  ? `Redirecting to Dashboard in ${countdown} second${countdown !== 1 ? 's' : ''}...`
                  : `Redirecting to Dashboard in ${countdown} second${countdown !== 1 ? 's' : ''}...`}
              </Typography>
              <Button
                variant="contained"
                onClick={redirectToSubscription}
                sx={{ mt: 2 }}
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card>
            <CardContent>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/pricing')}
                sx={{ mr: 2 }}
              >
                Back to Pricing
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default PaymentSuccess;

